/**
 * Expand/collapse “compare plans” accordion for Elite Plans landing & dashboards.
 * Owns expand state, analytics, and opening the benefits bottom sheet; section
 * UI is delegated to sibling components in this folder.
 */

import { useState } from 'react'
import type { CompareFeatureRow, CompareTitleParts } from '../../types/compare.types'
import { useElitePlansLanding } from '../../ElitePlansLandingContext'
import { ElitePlansEventId, trackElitePlansClick } from '../../analytics'
import {
  ELITE_PLANS_COMPARE_CHEVRON_COLLAPSED_SRC,
  ELITE_PLANS_COMPARE_CHEVRON_EXPANDED_SRC
} from '../../constants/assets'
import { ELITE_PLANS_ELITE_PRIME_TAB_ID } from '../../constants/planIds'
import { useBottomSheet } from '../../../../state-management/contexts/BottomSheetContext'
import { BenefitsInfoBottomSheet } from '../../../new-elite-landing/components/NewEliteLandingPageBenefits'
import AnimatedAccordion from '../../../../components/ui-components/AnimatedAccordion'
import { ElitePlansCompareColumnHeaders } from './ElitePlansCompareColumnHeaders'
import { ElitePlansCompareSectionCard } from './ElitePlansCompareSectionCard'
import { ElitePlansCompareTitle } from './ElitePlansCompareTitle'
import { compareBenefitInfoMap, compareBenefitTabTitles } from './compareAccordionHelpers'

const ELITE_PRIME_COMPARE_TITLE: CompareTitleParts = {
  leading: 'Compare Elite',
  connector: 'vs',
  trailing: 'Prime'
}

export function ElitePlansCompareAccordion() {
  const { content, activeTab } = useElitePlansLanding()
  // Elite landing: Free vs Elite; Prime tab keeps Elite vs Prime
  const title =
    activeTab.id === ELITE_PLANS_ELITE_PRIME_TAB_ID ? ELITE_PRIME_COMPARE_TITLE : content.compareTitle
  const sections = content.compareSections
  const [isExpanded, setIsExpanded] = useState(false)
  const { openBottomSheet, closeBottomSheet } = useBottomSheet()

  const handleToggle = () => {
    const next = !isExpanded
    setIsExpanded(next)
    trackElitePlansClick(ElitePlansEventId.CompareToggle, { expanded: next })
  }

  const handleFeatureInfo = (row: CompareFeatureRow) => {
    const tabTitle = row.benefitTabTitle
    if (!tabTitle || !compareBenefitInfoMap[tabTitle]) {
      return
    }

    trackElitePlansClick(ElitePlansEventId.FeatureOpen, {
      featureId: row.id,
      buttonLabel: row.label
    })

    openBottomSheet(
      <BenefitsInfoBottomSheet
        onDismiss={closeBottomSheet}
        initialTabTitle={tabTitle}
        tabTitles={compareBenefitTabTitles}
        sheetTitle='Unlock with Elite'
        benefitInfoMap={compareBenefitInfoMap}
      />
    )
  }

  return (
    <section className='bg-white'>
      <button
        type='button'
        aria-expanded={isExpanded}
        aria-controls='elite-plans-compare-panel'
        id='elite-plans-compare-trigger'
        onClick={handleToggle}
        className='!rounded-none flex w-full items-center justify-center gap-3 border-none bg-white px-6 text-left !pb-[30px]'
      >
        <ElitePlansCompareTitle title={title} />
        <img
          src={isExpanded ? ELITE_PLANS_COMPARE_CHEVRON_EXPANDED_SRC : ELITE_PLANS_COMPARE_CHEVRON_COLLAPSED_SRC}
          alt=''
          width={14}
          height={14}
          className='shrink-0'
          aria-hidden='true'
        />
      </button>

      <AnimatedAccordion isExpanded={isExpanded}>
        <div
          id='elite-plans-compare-panel'
          role='region'
          aria-labelledby='elite-plans-compare-trigger'
          className='px-3 pb-10 pt-0'
        >
          {sections.length === 0 ? (
            <p className='py-5 text-[13px] text-[color:var(--ep-ink-muted)]'>
              Comparison details coming soon.
            </p>
          ) : (
            <>
              <ElitePlansCompareColumnHeaders />
              <div className='mt-[11px] space-y-3'>
                {sections.map((section) => (
                  <ElitePlansCompareSectionCard
                    key={section.id}
                    section={section}
                    onFeatureInfo={handleFeatureInfo}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </AnimatedAccordion>
    </section>
  )
}
